// Reports' clock (spec 044 D70, D78): a day, a month and every range start at Cairo midnight,
// whatever the viewer's own time zone. Egypt keeps summer time (UTC+3 from the last Friday of
// April to the last Thursday of October, UTC+2 the rest of the year); Intl's zone data carries
// the switch, so no offset is written down here.
const wallClock = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Africa/Cairo",
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

// Cairo's wall clock at an instant: { y, m (1–12), d, h, mi, s }.
export const cairoParts = (date) => {
  const p = {};
  wallClock.formatToParts(date).forEach(({ type, value }) => {
    if (type !== "literal") p[type] = Number(value);
  });
  return { y: p.year, m: p.month, d: p.day, h: p.hour, mi: p.minute, s: p.second };
};

// How far Cairo's clock runs ahead of UTC at an instant, in ms.
const aheadOfUtc = (ms) => {
  const p = cairoParts(new Date(ms));
  return Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s) - ms;
};

// The instant a Cairo wall-clock time names. Parts roll over as Date.UTC's do (month 13 is
// January of the next year, day 0 the last day of the month before). Midnight on the day the
// clocks go forward does not exist in Cairo; it lands on the instant that day begins.
export const cairoTime = (y, m, d, h = 0, mi = 0, s = 0) => {
  const asUtc = Date.UTC(y, m - 1, d, h, mi, s);
  return new Date(asUtc - aheadOfUtc(asUtc - aheadOfUtc(asUtc)));
};

export const PERIODS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 days" },
  { key: "month", label: "This month" },
  { key: "3m", label: "3 months" },
  { key: "12m", label: "12 months" },
];
export const DEFAULT_PERIOD = "month";

const DAYS = { today: 1, "7d": 7 };
const MONTHS_LONG = { month: 1, "3m": 3, "12m": 12 };
export const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MONTH_NAME = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Cairo's clock at `p` moved back one period; null when that day does not exist (31 March
// back one month), which reportWindows reads as "the whole period before".
const onePeriodBack = (p, key) => {
  if (DAYS[key]) return cairoTime(p.y, p.m, p.d - DAYS[key], p.h, p.mi, p.s);
  const first = new Date(Date.UTC(p.y, p.m - 1 - MONTHS_LONG[key], 1));
  const y = first.getUTCFullYear();
  const m = first.getUTCMonth() + 1;
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return p.d > lastDay ? null : cairoTime(y, m, p.d, p.h, p.mi, p.s);
};

// D70, D78: the windows a period counts at `now`, each { from, to } with `to` exclusive.
// `whole` is the whole period, the days still ahead included (lessons, active teachers);
// `soFar` is its start up to now (money, new students). Each carries `prev`, the stretch it is
// compared with: the whole period before, or that period's start up to the same point in it.
export const reportWindows = (key, now = new Date()) => {
  const p = cairoParts(now);
  let from;
  let to;
  let prevFrom;
  if (DAYS[key]) {
    const n = DAYS[key];
    from = cairoTime(p.y, p.m, p.d - (n - 1));
    to = cairoTime(p.y, p.m, p.d + 1);
    prevFrom = cairoTime(p.y, p.m, p.d - (2 * n - 1));
  } else {
    const n = MONTHS_LONG[key];
    from = cairoTime(p.y, p.m - (n - 1), 1);
    to = cairoTime(p.y, p.m + 1, 1);
    prevFrom = cairoTime(p.y, p.m - (2 * n - 1), 1);
  }
  return {
    key,
    now,
    whole: { from, to, prev: { from: prevFrom, to: from } },
    soFar: { from, to: now, prev: { from: prevFrom, to: onePeriodBack(p, key) || from } },
  };
};

// "29 Sep 2026", "1 – 29 Sep 2026", "1 Jul – 29 Sep 2026", "1 Oct 2025 – 29 Sep 2026": the
// Cairo days a window covers (its `to` is exclusive).
export const rangeLabel = ({ from, to }) => {
  const a = cairoParts(from);
  const b = cairoParts(new Date(Math.max(from.getTime(), to.getTime() - 1)));
  const day = (x) => `${x.d} ${MONTH_SHORT[x.m - 1]}`;
  if (a.y !== b.y) return `${day(a)} ${a.y} – ${day(b)} ${b.y}`;
  if (a.m !== b.m) return `${day(a)} – ${day(b)} ${b.y}`;
  if (a.d !== b.d) return `${a.d} – ${day(b)} ${b.y}`;
  return `${day(b)} ${b.y}`;
};

// "2026-09-29": the Cairo date, for a file name.
export const cairoDateKey = (date) => {
  const p = cairoParts(date);
  return `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`;
};

const labelOf = (key) => PERIODS.find((x) => x.key === key).label;

// The period in words: "today", "the last 7 days", "September", "the last 3 months".
export const periodWords = (key, now = new Date()) => {
  if (key === "today") return "today";
  if (key === "month") return MONTH_NAME[cairoParts(now).m - 1];
  return `the last ${labelOf(key)}`;
};

// D76: the line under the title.
export const greeting = (name, key, now = new Date()) => {
  const first = String(name || "").trim().split(/\s+/)[0];
  const verb = key === "today" || key === "month" ? "is" : "are";
  return `Assalamu alaikum${first ? `, ${first}` : ""}. Here’s how ${periodWords(key, now)} ${verb} going.`;
};

// D77: New students' second line.
export const signedUpLine = (key) => {
  if (key === "today") return "Signed up today";
  if (key === "month") return "Signed up this month";
  return `Signed up in the last ${labelOf(key)}`;
};
